import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { EfficiencyFlagProps, ProductPriceProps } from '@packages/shared';
// import { ProductCard } from '@packages/modules';
import { useTracking } from '@packages/tracking';
import { defineMessages, useIntl } from 'react-intl';
import { ProductCard } from '../../copiedComponentsFromModulesToAvoidDependencyCycles/ProductCard';
import { getClickTrackingProductsURLExtension } from '../../helpers/getClickTrackingURLExtension';
import prepareEECproductClick from '../../helpers/prepareEecProductClick';
import type { PageTemplate, RdeType } from '../../types';

const ProductRecommendationFragment = graphql(/* GraphQL */ `
  fragment ProductRecommendationFragment on ProductRecommendation {
    product {
      additionalFlags {
        src
        text
        type
      }
      akl
      brand {
        name
      }
      defaultCategory
      downloads {
        link
        type
      }
      link
      media {
        type
        url
      }
      name
      orderNumber
      powerEfficiencyFlags {
        color
        hasOldLabel
        level
        link
        text
      }
      price {
        amount
        currency
        isStartPrice
        saving {
          type
          value
        }
        uvp
        unit
        value
        valueOld
        valueUnit
      }
      sku
      tireEfficiency {
        flags {
          level
          type
        }
        link
      }
    }
    tracking {
      token
      trackingUrls {
        url
        purpose
      }
      logic
    }
  }
`);

/**
 * This maps the type from our product data to the correct format for the component `EfficiencyFlag`
 */
const tireEfficiencyTypeMap: { [key: string]: EfficiencyFlagProps['type'] } = {
  WETGRIP: 'wetGrip',
  FUELEFFICIENCY: 'fuelEfficiency',
  EXTERNALROLLINGNOISE: 'externalRollingNoise',
};

const messages = defineMessages({
  WETGRIP: {
    defaultMessage: 'Nasshaftung',
    id: 'recoProductCard.type.wetgrip',
  },
  FUELEFFICIENCY: {
    defaultMessage: 'Kraftstoffeffizienz',
    id: 'recoProductCard.type.fuelEfficiency',
  },
  EXTERNALROLLINGNOISE: {
    defaultMessage: 'Rollgeräusch',
    id: 'recoProductCard.type.externalRollingNoise',
  },
  PRODUCT_DATASHEET: {
    defaultMessage: 'Produktdatenblatt',
    id: 'recoProductCard.type.productdatasheet',
  },
  uvp: {
    defaultMessage: 'UVP:',
    id: 'recoProductCard.price.uvp',
  },
  saving: {
    defaultMessage: 'statt',
    id: 'recoProductCard.price.saving',
  },
});

export type RecoProductCardProps = {
  /**
   * Reco Position on page.
   * If there are multiple recos you have to provide this prop (starting with 0 for first reco).
   *
   * @default 0
   */
  elementIndex: number;
  /**
   * pageTemplate for products tracking
   */
  pageTemplate: PageTemplate;
  /**
   * position of product
   */
  position: number;
  recommendationData: FragmentType<typeof ProductRecommendationFragment>;
  /**
   * number of products
   */
  productsLength: number;
  /**
   * Recommendation type
   */
  rdeType: RdeType;
};

export const RecoProductCard = ({
  elementIndex,
  pageTemplate,
  position,
  recommendationData,
  productsLength,
  rdeType,
}: RecoProductCardProps) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const { product, tracking } = getFragmentData(ProductRecommendationFragment, recommendationData);

  if (!product) return null;

  const { price } = product;

  const imageUrl = product.media.find((ele) => ele.type === 'IMAGE')?.url;
  const datasheets = product.downloads.filter((ele) => ele.type === 'PRODUCT_DATASHEET');

  const criteoLink = tracking.trackingUrls.find((ele) => ele.purpose === 'ProductPage')?.url;

  const additionalPowerFlags = product.powerEfficiencyFlags
    ? product.powerEfficiencyFlags.map(
        (flag): EfficiencyFlagProps => ({
          type: flag.hasOldLabel ? 'energyEfficiencyOld' : 'energyEfficiency',
          labelColor: flag.color as EfficiencyFlagProps['labelColor'],
          text: flag.level,
          overlay: { title: flag.text, imgUrl: flag.link },
        }),
      )
    : [];

  const additionalTireFlags =
    product.tireEfficiency && product.tireEfficiency.flags
      ? product.tireEfficiency.flags.map(
          (flag): EfficiencyFlagProps => ({
            type: tireEfficiencyTypeMap[flag.type],
            text: flag.level,
            overlay: {
              title: formatMessage(messages[flag.type]),
              imgUrl: product.tireEfficiency!.link!,
            },
          }),
        )
      : [];

  return (
    <ProductCard
      sku={product.sku}
      minimal
      title={product.name}
      brand={product.brand?.name || ''}
      image={`${imageUrl}?$PL_landscape_hq$`}
      productLink={
        criteoLink ||
        `${product.link}${getClickTrackingProductsURLExtension({
          cid: product.defaultCategory || '',
          pageTemplate,
          rdeType,
          recoLogic: tracking.logic,
          sourceArticleNo: '',
          outputElementNo: elementIndex,
          productsToLoad: productsLength,
          targetOrderNumber: product.orderNumber,
          position: position + 1,
        })}`
      }
      onClick={() => {
        dispatchGTMEvent(
          prepareEECproductClick({
            brand: product.brand?.name || '',
            list: rdeType,
            name: product.name,
            pid: product.orderNumber,
            position: position + 1,
            priceValue: price.value ?? 0,
            trackingToken: tracking.token || undefined,
          }),
        );
      }}
      // TODO: adapt ProductPrice props to data structure after ProductPrice is adapted and fragmentized
      price={{
        amount: price.amount ?? undefined,
        currency: price.currency,
        price: price.value ?? 0,
        saving: price.saving?.value,
        oldPrice: price.valueOld ?? undefined,
        discountType: (price.saving?.type as ProductPriceProps['discountType']) ?? undefined,
        unit: price.unit || undefined,
        unitPrice: price.valueUnit || undefined,
        isStartPrice: !!price.isStartPrice,
        uvpLabel: price.uvp
          ? formatMessage(messages.uvp)
          : (price.saving !== null && formatMessage(messages.saving)) || undefined,
      }}
      // flags are being disabled for now, since the tenants are working on a better solution for displaying them
      // flags={product.additionalFlags.map(({ text, type }) => ({
      //   text,
      //   type: type as FlagProps['type'],
      // }))}
      additionalFlags={
        additionalPowerFlags.length > 0 || additionalTireFlags.length > 0
          ? [...additionalPowerFlags, ...additionalTireFlags]
          : undefined
      }
      datasheet={
        datasheets.length > 0
          ? { name: formatMessage(messages.PRODUCT_DATASHEET), url: datasheets[0].link }
          : undefined
      }
      shrinkToFit
    />
  );
};
